import React, { useState } from 'react'
import {
  StepContainerWide,
  StepHeading,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldHelperText,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  DefaultsList,
  DefaultsListItem,
  DefaultsItemTitle,
  DefaultsItemBody,
} from '../setupStyled'

interface ServiceDefaultsProps {
  serviceName: string
  onServiceNameChange: (name: string) => void
  onNext: () => void
  onBack: () => void
}

/**
 * Fifth step: name the service and review the secure-by-default posture
 * being applied. This step is read-only summary + a single display-name
 * field — it does not call the setup client (the secure defaults it
 * describes are already the server's baked-in fail-closed configuration,
 * not something this wizard grants or configures).
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI TextField/Card/List onto a
 * minimal local themed form + a themed defaults list (see setupStyled.ts);
 * no API-call/state logic changed.
 */
export const ServiceDefaults = ({
  serviceName,
  onServiceNameChange,
  onNext,
  onBack,
}: ServiceDefaultsProps): JSX.Element => {
  const [localName, setLocalName] = useState(serviceName)

  const handleNext = (): void => {
    onServiceNameChange(localName)
    onNext()
  }

  return (
    <StepContainerWide>
      <StepHeading>Service defaults</StepHeading>
      <FieldGroup>
        <FieldLabel htmlFor="service-name">Service name</FieldLabel>
        <FieldInput
          id="service-name"
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
        />
        <FieldHelperText>
          Shown to users on sign-in and consent screens.
        </FieldHelperText>
      </FieldGroup>
      <div>
        <StepHeading as="h3">
          Secure defaults applied to this deployment
        </StepHeading>
        <DefaultsList>
          <DefaultsListItem>
            <DefaultsItemTitle>
              Step-up (AAL3) required for privileged PAM actions
            </DefaultsItemTitle>
            <DefaultsItemBody>
              Activating a PAM grant or other dangerous-activity operation
              always requires a fresh phishing-resistant assertion, regardless
              of how the admin originally signed in.
            </DefaultsItemBody>
          </DefaultsListItem>
          <DefaultsListItem>
            <DefaultsItemTitle>
              Phishing-resistant MFA required for administrators
            </DefaultsItemTitle>
            <DefaultsItemBody>
              The policy this wizard just enforced (WebAuthn/mTLS) is the
              standing requirement for every future admin, not just the
              bootstrap account.
            </DefaultsItemBody>
          </DefaultsListItem>
          <DefaultsListItem>
            <DefaultsItemTitle>No SMS or email-based factors</DefaultsItemTitle>
            <DefaultsItemBody>
              Phishable out-of-band factors are not offered anywhere in this
              deployment&apos;s MFA policy.
            </DefaultsItemBody>
          </DefaultsListItem>
          <DefaultsListItem>
            <DefaultsItemTitle>
              Dangerous-activity invariant enforced fail-closed
            </DefaultsItemTitle>
            <DefaultsItemBody>
              Break-glass and emergency-role paths remain fully audited and
              time-boxed; nothing configured in this wizard weakens that
              invariant.
            </DefaultsItemBody>
          </DefaultsListItem>
        </DefaultsList>
      </div>
      <ButtonRow>
        <SecondaryButton type="button" onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton type="button" onClick={handleNext} disabled={!localName}>
          Next
        </PrimaryButton>
      </ButtonRow>
    </StepContainerWide>
  )
}
